import React from 'react';
import { Users, ChevronRight } from 'lucide-react';
import type { Client, Role } from '../../types';

interface ClientSelectionPanelProps {
  clients: Client[];
  selectedClient: Client | null;
  selectedRole: Role | null;
  loading: boolean;
  language: 'en' | 'ar';
  strings: any;
  onClientSelect: (client: Client) => void;
}

const ClientSelectionPanel: React.FC<ClientSelectionPanelProps> = ({
  clients,
  selectedClient,
  selectedRole,
  loading,
  language,
  strings,
  onClientSelect
}) => {
  return (
    <div className="lg:col-span-1">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
            {strings.legalFiles.selectClient}
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            {selectedRole 
              ? language === 'ar' 
                ? `تم العثور على ${clients.length} موكل${clients.length !== 1 ? 'ين' : ''}`
                : `${clients.length} client${clients.length !== 1 ? 's' : ''} found`
              : language === 'ar' 
                ? 'اختر الدور أولاً'
                : 'Select a role first'
            }
          </p>
        </div>
        
        <div className="p-4">
          {!selectedRole ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                {language === 'ar' ? 'اختر الدور أولاً' : 'Select a role first'}
              </p>
            </div>
          ) : loading ? (
            <div className="text-center py-8">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                {language === 'ar' ? 'جاري تحميل الموكلين...' : 'Loading clients...'}
              </p>
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                {language === 'ar' ? 'لا يوجد موكلون' : 'No clients found'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {clients.map((client) => {
                const isSelected = selectedClient?.id === client.id;
                
                return (
                  <div
                    key={client.id}
                    className={`p-4 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                      isSelected 
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-primary shadow-sm' 
                        : 'bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                    }`}
                    onClick={() => onClientSelect(client)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-slate-800 dark:text-white text-sm truncate">
                          {client.name}
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {client.mobileNumber || 'No phone'}
                        </p>
                      </div>
                      <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                        isSelected ? 'rotate-90' : ''
                      }`} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientSelectionPanel;
